import { onAuthStateChanged } from "firebase/auth"
import { DEFAULT_AGENT_ID } from "../constants/agent"
import { auth } from "../app/App"
import { Agent } from "../types/Agent"
import { ApiVersion, Endpoints, Method, SecondPathParams } from "../types/Api"

export const isLocalDev = process.env.NODE_ENV === "development"

export const isDev = isLocalDev || window.location.href.includes("dev")

export const isStaging = window.location.href.includes("staging")

export const isProduction = !isLocalDev && !isDev && !isStaging

export const backendUrl = isLocalDev
  ? "http://127.0.0.1:8000"
  : isDev
    ? "https://api-dev.bey.chat"
    : isStaging
      ? "https://api-staging.bey.chat"
      : "https://api.bey.chat"

const DEFAULT_API_VERSION = ApiVersion.v3
const FALLBACK_API_VERSION = ApiVersion.v2

export interface VerifyTokenResponse {
  uid: string
  email: string
  name: string
  customerId?: string
  subscriptionState?: string
  subscriptionPlan?: string
  customToken?: string
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public response?: any,
  ) {
    super(message)
    this.name = "ApiError"
  }
}

const waitForAuth = (): Promise<void> => {
  return new Promise((resolve) => {
    if (auth.currentUser) {
      resolve()
      return
    }

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        unsubscribe()
        resolve()
      }
    })

    setTimeout(() => {
      unsubscribe()
      resolve()
    }, 5000)
  })
}

type CallApiOptions = {
  endpoint: Endpoints | string
  method?: Method
  id?: string
  body?: any
  secondPathParam?: SecondPathParams
  token?: string
  apiVersion?: ApiVersion
}

const buildUrl = (
  apiVersion: ApiVersion,
  endpoint: string,
  id?: string,
  secondPathParam?: SecondPathParams,
): string => {
  const baseUrl = `${backendUrl}/${apiVersion}/`
  if (secondPathParam && !id) return `${baseUrl}${endpoint}/${secondPathParam}`
  if (secondPathParam) return `${baseUrl}${endpoint}/${id}/${secondPathParam}`
  if (id) return `${baseUrl}${endpoint}/${id}`
  return `${baseUrl}${endpoint}`
}

const fetchWithVersion = async (
  apiVersion: ApiVersion,
  endpoint: string,
  method: Method,
  authToken: string,
  id?: string,
  body?: any,
  secondPathParam?: SecondPathParams,
) => {
  const url = buildUrl(apiVersion, endpoint, id, secondPathParam)

  const response = await fetch(url, {
    method,
    body: body ? JSON.stringify(body) : undefined,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${authToken}`,
    },
  })

  if (!response.ok) {
    let json: any = null
    try {
      json = await response.json()
    } catch {
      console.error("Error parsing JSON:", response.statusText)
    }
    throw new ApiError(
      json?.detail || `HTTP Error ${response.status}`,
      response.status,
      json,
    )
  }

  if (
    response.status === 204 ||
    response.headers.get("content-length") === "0"
  ) {
    return undefined
  }

  return response.json()
}

const callApi = async ({
  endpoint,
  method = Method.GET,
  id,
  body,
  secondPathParam,
  token,
  apiVersion,
}: CallApiOptions) => {
  let authToken = token

  if (!authToken) {
    if (!auth.currentUser) {
      await waitForAuth()
    }
    authToken = auth.currentUser ? await auth.currentUser.getIdToken() : null
  }

  if (!authToken) {
    throw new ApiError("Authentication failed - no token available", 401)
  }

  const version = apiVersion ?? DEFAULT_API_VERSION
  const canFallback = !apiVersion

  try {
    return await fetchWithVersion(
      version,
      endpoint,
      method,
      authToken,
      id,
      body,
      secondPathParam,
    )
  } catch (error) {
    if (canFallback && error instanceof ApiError && error.status === 404) {
      console.warn(
        `[API] ${version}/${endpoint} returned 404, falling back to ${FALLBACK_API_VERSION}`,
      )
      return await fetchWithVersion(
        FALLBACK_API_VERSION,
        endpoint,
        method,
        authToken,
        id,
        body,
        secondPathParam,
      )
    }
    throw error
  }
}

export const verifyToken = async (
  token: string,
): Promise<VerifyTokenResponse> => {
  return callApi({
    endpoint: Endpoints.creator,
    method: Method.POST,
    secondPathParam: SecondPathParams.verifyToken,
    token,
  })
}

export interface LivekitRoomInfo {
  url: string
  token: string
  call_id: string
}

export const LIVEKIT_ROOM_STORAGE_KEY = "livekit_room_info"

export const getStoredCallId = (): string | null => {
  const stored = sessionStorage.getItem(LIVEKIT_ROOM_STORAGE_KEY)
  if (!stored) return null
  try {
    const roomInfo: LivekitRoomInfo = JSON.parse(stored)
    return roomInfo.call_id
  } catch {
    return null
  }
}

export const createLivekitRoom = async (
  agentId: string,
): Promise<LivekitRoomInfo> => {
  const roomInfo: LivekitRoomInfo = await callApi({
    method: Method.POST,
    endpoint: `${Endpoints.rooms}?agent_id=${agentId}`,
  })
  sessionStorage.setItem(LIVEKIT_ROOM_STORAGE_KEY, JSON.stringify(roomInfo))
  return roomInfo
}

export const getAgent = async (agent_id: string): Promise<Agent> => {
  return callApi({
    endpoint: Endpoints.agent,
    id: agent_id,
    secondPathParam: SecondPathParams.manifest,
  })
}

export const getDefaultAgentId = DEFAULT_AGENT_ID
